import { PageWrapper } from 'components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMatch } from 'react-router';
import { useGetMerchantQuery } from 'services/merchants';
import { useGetRefundWalletTopupQuery } from 'services/refunds';

import useGetUuid from 'components/accountSettings/utils';
import LoadingIndicator from 'components/loadingIndicator';

import { ErrorText } from 'pages/error/styles';
import { hasUserS4cRole } from 'pages/orders/view/utils';
import { useRedirectOnAccountSwitch } from 'pages/utils/useRedirectOnAccountSwitch';

import RoutePath from 'router/path';

import { selectUserLevel } from 'state/slices/userSlice';

import { ReduxToolkitError } from 'types/http';

import { BREADCRUMBS, LABELS } from './keys';
import TopUpDetails from './toptup';

export const CustodialWalletTransactionContainer = () => {
  const { t } = useTranslation();
  useRedirectOnAccountSwitch(RoutePath.Ramp.Orders.Root);

  const match = useMatch(RoutePath.Pwc.RefundAccountTransactions.ById)?.params;
  const transactionUuid = match?.transactionUuid;
  const merchantUuid = match?.merchantUuid;
  const uuid = useGetUuid() || merchantUuid;
  const userLevel = useSelector(selectUserLevel);
  const isS4cUser = hasUserS4cRole(userLevel);

  const {
    data: transaction,
    isLoading,
    error,
  } = useGetRefundWalletTopupQuery(
    { merchantUuid: uuid ?? '', topupUuid: transactionUuid ?? '' },
    {
      skip: !uuid || !transactionUuid,
    },
  );

  const { data: merchantResponse } = useGetMerchantQuery(uuid!, {
    skip: !uuid,
  });

  const breadcrumbs = BREADCRUMBS.map((item) => t(item));

  if (isLoading) {
    return (
      <PageWrapper pageTitle={t(LABELS.PAGE_TITLE)} breadcrumbs={breadcrumbs}>
        <LoadingIndicator padded />
      </PageWrapper>
    );
  }

  if (!transaction && error) {
    return (
      <PageWrapper pageTitle={t(LABELS.PAGE_TITLE)} breadcrumbs={breadcrumbs}>
        <ErrorText>{(error as ReduxToolkitError)?.data?.errorMessage ?? t(LABELS.ERRORS.FETCHING_TRANSACTION)}</ErrorText>
      </PageWrapper>
    );
  }

  if (!transaction) {
    return null;
  }

  return (
    <PageWrapper breadcrumbs={breadcrumbs} pageTitle={t(LABELS.PAGE_TITLE)}>
      <TopUpDetails transaction={transaction} currency={merchantResponse?.data.mainFiatCurrency} isS4cUser={isS4cUser} />
    </PageWrapper>
  );
};

export default CustodialWalletTransactionContainer;
